import { notifications } from "@mantine/notifications";
import type { AxiosError, InternalAxiosRequestConfig } from "axios";
import axios from "axios";

import { ROUTES } from "@/app/router/routes";

import { clearTokens, getAccessToken, getRefreshToken, setTokens } from "./token";

type RetriableRequestConfig = InternalAxiosRequestConfig & { _retry?: boolean };

type ApiErrorData = {
  description_fa?: string;
  message?: string;
};

type ApiError = AxiosError<ApiErrorData>;

type RefreshTokenResponse = {
  access_token: string;
  refresh_token: string;
};

export const TENANT_ID = import.meta.env.VITE_TENANT_ID || "";

const BASE_URL = import.meta.env.VITE_GATEWAY_BASE_URL || "";

const api = axios.create({
  baseURL: BASE_URL,
});

function attachAuthHeaders(config: InternalAxiosRequestConfig) {
  const accessToken = getAccessToken();

  if (accessToken) {
    config.headers.set("Authorization", `Bearer ${accessToken}`);
  }

  if (TENANT_ID) {
    config.headers.set("X-Tenant-Id", TENANT_ID);
  }

  return config;
}

let refreshRequest: Promise<void> | null = null;

async function requestNewTokens() {
  const refreshToken = getRefreshToken();

  if (!refreshToken) {
    throw new Error("رفرش توکن موجود نیست");
  }

  const { data } = await axios.post<{ data: RefreshTokenResponse }>(
    `${BASE_URL}/api/v1/auth/refresh`,
    { refresh_token: refreshToken },
    { headers: { "X-Tenant-Id": TENANT_ID } },
  );

  setTokens(data.data.access_token, data.data.refresh_token);
}

function refreshTokens() {
  refreshRequest ??= requestNewTokens().finally(() => {
    refreshRequest = null;
  });

  return refreshRequest;
}

function endSession() {
  clearTokens();
  window.location.assign(`/${ROUTES.LOGIN}`);
}

function notifyForbidden(serverMessage?: string) {
  notifications.show({
    title: "خطای دسترسی",
    message: serverMessage || "دسترسی به این بخش برای شما مجاز نیست",
    color: "error.5",
  });
}

async function retryWithNewTokens(error: ApiError) {
  const request = error.config as RetriableRequestConfig | undefined;

  if (!request || request._retry || !getRefreshToken()) {
    endSession();

    return Promise.reject(error);
  }

  request._retry = true;

  try {
    await refreshTokens();

    return await api(request);
  } catch (refreshError) {
    endSession();

    return Promise.reject(refreshError);
  }
}

function handleApiError(error: ApiError) {
  const errorData = error.response?.data;
  const serverMessage = errorData?.description_fa || errorData?.message;

  if (serverMessage) {
    error.message = serverMessage;
  }

  if (error.response?.status === 403) {
    notifyForbidden(serverMessage);
  }

  if (error.response?.status === 401) {
    return retryWithNewTokens(error);
  }

  return Promise.reject(error);
}

api.interceptors.request.use(attachAuthHeaders);
api.interceptors.response.use((response) => response, handleApiError);

export default api;
